/** Pure helpers for dashboard link tiles: grouping, reordering and URL clean-up. */
export interface LinkTile {
  id: string;
  title: string;
  url: string;
  group: string;
  order: number;
}

export type LinkScope = { kind: "workspace"; workspaceId: string } | { kind: "me" };

export interface LinkGroup<T extends LinkTile = LinkTile> {
  group: string;
  links: T[];
}

/** Groups in the order they first appear (the api sorts by group, then order). */
export function groupLinks<T extends LinkTile>(links: readonly T[]): Array<LinkGroup<T>> {
  const groups = new Map<string, T[]>();
  for (const link of links) {
    const list = groups.get(link.group);
    if (list === undefined) groups.set(link.group, [link]);
    else list.push(link);
  }
  return [...groups.entries()].map(([group, items]) => ({ group, links: items }));
}

/**
 * Moves a link one place up or down inside its group and returns every id in the new order
 * (what `dashboard.reorderLinks` takes), or null when it is already at that end.
 */
export function moveLink(links: readonly LinkTile[], id: string, direction: "up" | "down"): string[] | null {
  const groups = groupLinks(links);
  const group = groups.find((g) => g.links.some((l) => l.id === id));
  if (group === undefined) return null;
  const from = group.links.findIndex((l) => l.id === id);
  const to = direction === "up" ? from - 1 : from + 1;
  if (to < 0 || to >= group.links.length) return null;
  const reordered = [...group.links];
  const [moved] = reordered.splice(from, 1);
  if (moved === undefined) return null;
  reordered.splice(to, 0, moved);
  group.links = reordered;
  return groups.flatMap((g) => g.links.map((l) => l.id));
}

/** Applies an id order to the cached list (optimistic update while the api saves it). */
export function applyOrder<T extends LinkTile>(links: readonly T[], orderedIds: readonly string[]): T[] {
  const position = new Map(orderedIds.map((id, i) => [id, i]));
  return links
    .map((l) => {
      const order = position.get(l.id);
      return order === undefined ? l : { ...l, order };
    })
    .sort((a, b) => a.group.localeCompare(b.group) || a.order - b.order);
}

/** Accepts `example.com/path` as well as full URLs; null when it is not an http(s) URL. */
export function normalizeUrl(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) return null;
  const withScheme = /^[a-z][a-z\d+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withScheme);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    if (!url.hostname.includes(".") && url.hostname !== "localhost") return null;
    return url.toString();
  } catch {
    return null;
  }
}

export function hostOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

export function faviconUrl(url: string): string {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(hostOf(url))}&sz=64`;
}
